import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Doctor } from 'src/API';
import { PAMPRestAPIService } from 'src/app/core/api/pamp-rest-api.service';
import {
  buildQuestionnaireSelectionCategories,
  QuestionnaireSelectionCategory
} from 'src/app/core/api/questionnaire-selection.util';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';
import { StaffService } from 'src/app/core/api/staff.service';
import { PampAssessmentTodoTemplate, pampAssessmentTodoTemplatesFromResponse } from 'src/app/core/models/pamp-todo.model';
import {
  MAX_ONE_CLICK_QRE_SET_NAME_LENGTH,
  OneClickQRESet,
  OneClickQRESetItem
} from 'src/app/core/models/one-click-qre-set.model';
import { OneClickQRESetService } from 'src/app/core/services/one-click-qre-set.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { IntakeFormService } from 'src/app/intake-form/intake-form.service';
import { ConsentSendService } from 'src/app/core/api/consent-send.service';
import {
  addControlsIfMissing,
  applyCompatibilityToFormGroup,
  collectSelectedSetItems,
  QuestionnaireLabelMaps,
  setFormGroupSelectedIds
} from '../questionnaire-category-picker/questionnaire-selection-form';

export interface OneClickQRESetEditorDialogData {
  set?: OneClickQRESet;
}

export interface OneClickQRESetEditorDialogResult {
  name: string;
  items: OneClickQRESetItem[];
}

@Component({
  selector: 'csi-one-click-qre-set-editor-dialog',
  templateUrl: './one-click-qre-set-editor-dialog.component.html',
  styleUrls: ['./one-click-qre-set-editor-dialog.component.css']
})
export class OneClickQRESetEditorDialogComponent implements OnInit, OnDestroy {
  public nameControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(MAX_ONE_CLICK_QRE_SET_NAME_LENGTH)
  ]);
  public nameErrorKey = '';
  public itemsErrorKey = '';
  public maxNameLength = MAX_ONE_CLICK_QRE_SET_NAME_LENGTH;
  public nameLength = 0;
  public questionnaireTypesFormGroup = new FormGroup({});
  public pampTodosFormGroup = new FormGroup({});
  public questionnaireSelectionCategories: QuestionnaireSelectionCategory[] = [];
  public pampAssessmentTodoTemplates: PampAssessmentTodoTemplate[] = [];
  public pampTodoKeyToFriendlyNameMap: { [key: string]: string } = {};
  public showPatientAppCategory = false;
  public includeIntake = true;
  public dialogTitle = 'Add Template';
  public confirmLabel = 'Save';
  public standardKeyToName: { [key: string]: string } = {};
  public intakeKeyToName: { [key: string]: string } = {};

  constructor(
    public dialogRef: MatDialogRef<
      OneClickQRESetEditorDialogComponent,
      OneClickQRESetEditorDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) public data: OneClickQRESetEditorDialogData,
    private oneClickQRESetService: OneClickQRESetService,
    private questionnaireRequestService: QuestionnaireRequestService,
    private intakeFormService: IntakeFormService,
    private consentSendService: ConsentSendService,
    private pampRestApiService: PAMPRestAPIService,
    private staffService: StaffService,
    private appStateService: AppStateService
  ) {}

  public static open(matDialog: MatDialog, data: OneClickQRESetEditorDialogData = {}) {
    return matDialog.open<
      OneClickQRESetEditorDialogComponent,
      OneClickQRESetEditorDialogData,
      OneClickQRESetEditorDialogResult
    >(OneClickQRESetEditorDialogComponent, {
      width: '36rem',
      maxHeight: '90vh',
      data
    });
  }

  ngOnInit() {
    if (this.data && this.data.set) {
      this.dialogTitle = 'Edit Template';
      this.nameControl.setValue(this.data.set.name);
    }

    this.standardKeyToName = {};
    const friendlyNames = this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap || {};
    Object.keys(friendlyNames).forEach(key => {
      if (key !== 'CUSTOM' && friendlyNames[key]) {
        this.standardKeyToName[key] = friendlyNames[key];
      }
    });
    this.intakeKeyToName = this.buildIntakeKeyToName();
    addControlsIfMissing(this.questionnaireTypesFormGroup, Object.keys(this.standardKeyToName));
    addControlsIfMissing(
      this.questionnaireTypesFormGroup,
      Object.keys(this.questionnaireRequestService.customQuestionnaireKeyToFriendlyNameMap || {})
    );
    addControlsIfMissing(
      this.questionnaireTypesFormGroup,
      Object.keys(this.consentSendService.getConsentFormKeyToFriendlyNameMap())
    );
    addControlsIfMissing(this.questionnaireTypesFormGroup, Object.keys(this.intakeKeyToName));

    if (this.data && this.data.set) {
      const selectedIds: { [id: string]: boolean } = {};
      this.data.set.items.forEach(item => {
        selectedIds[item.id] = true;
      });
      setFormGroupSelectedIds(this.questionnaireTypesFormGroup, selectedIds);
    }

    applyCompatibilityToFormGroup(this.questionnaireTypesFormGroup, this.includeIntake);

    this.refreshCategories();
    this.fetchPampAssessmentTodoTemplates();
    this.nameLength = (this.nameControl.value || '').length;
    this.nameControl.valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      this.nameLength = (value || '').length;
      if (this.nameErrorKey) {
        this.refreshNameError();
      }
    });
  }

  ngOnDestroy() {}

  public trackByTodoId(_index: number, todo: PampAssessmentTodoTemplate): string {
    return todo.id;
  }

  public onQuestionnaireSelectionChange() {
    applyCompatibilityToFormGroup(this.questionnaireTypesFormGroup, this.includeIntake);
    this.refreshCategories();
    this.itemsErrorKey = '';
  }

  public onPampItemChange() {
    this.itemsErrorKey = '';
  }

  public onSave() {
    const trimmedName = this.refreshNameError();
    if (this.nameErrorKey) {
      return;
    }

    const items = this.collectSelectedItems();
    const itemsError = this.oneClickQRESetService.validateItems(items, this.includeIntake);
    if (itemsError) {
      this.itemsErrorKey = itemsError;
      return;
    }

    this.dialogRef.close({
      name: trimmedName,
      items
    });
  }

  private refreshNameError(): string {
    const excludeSetId = this.data && this.data.set ? this.data.set.id : undefined;
    const nameValidation = this.oneClickQRESetService.validateName(
      this.nameControl.value || '',
      excludeSetId
    );
    this.nameErrorKey = nameValidation.valid ? '' : nameValidation.errorKey;
    return nameValidation.trimmedName;
  }

  private refreshCategories() {
    this.questionnaireSelectionCategories = buildQuestionnaireSelectionCategories({
      standardKeyToName: this.standardKeyToName,
      customKeyToName:
        this.questionnaireRequestService.customQuestionnaireKeyToFriendlyNameMap || {},
      consentKeyToName: this.consentSendService.getConsentFormKeyToFriendlyNameMap(),
      intakeKeyToName: this.intakeKeyToName,
      includeIntake: this.includeIntake
    });
  }

  private buildIntakeKeyToName(): { [key: string]: string } {
    const schemaMap = Object.entries({
      ...this.intakeFormService.defaultIntakeFormTemplateSchemas,
      ...this.questionnaireRequestService.customIntakeFormKeyToSchemaMap
    }).reduce((acc, entry) => {
      const key = entry[0];
      const value = entry[1];
      acc[key] = value && value['schema'] ? value['schema'] : value;
      return acc;
    }, {} as { [key: string]: { name?: string } });

    return Object.entries(schemaMap).reduce((acc, entry) => {
      const key = entry[0];
      const schema = entry[1];
      acc[key] = schema && schema.name ? schema.name : key;
      return acc;
    }, {} as { [key: string]: string });
  }

  private fetchPampAssessmentTodoTemplates() {
    if (!this.appStateService.getIsPatientAppActive()) {
      return;
    }
    const staff = this.staffService.staff;
    if (staff && staff.id && staff.clinic) {
      this.loadAssessmentTodosForStaff(staff);
      return;
    }
    this.staffService
      .getLoggedInStaff()
      .pipe(untilDestroyed(this))
      .subscribe(loggedInStaff => {
        this.loadAssessmentTodosForStaff(loggedInStaff);
      });
  }

  private loadAssessmentTodosForStaff(staff: Doctor) {
    if (!staff || !staff.id || !staff.clinic) {
      return;
    }
    this.pampRestApiService
      .getPampTodos(staff.id, staff.clinic.id, 'assessment')
      .pipe(untilDestroyed(this))
      .subscribe((todoTemplates: PampAssessmentTodoTemplate[] | { data?: PampAssessmentTodoTemplate[] }) => {
        const templates = pampAssessmentTodoTemplatesFromResponse(todoTemplates);
        this.pampAssessmentTodoTemplates = templates;
        const groupControls: { [id: string]: FormControl } = {};
        const nameMap: { [id: string]: string } = {};
        const selectedFromSet: { [id: string]: boolean } = {};
        if (this.data && this.data.set) {
          this.data.set.items.forEach(item => {
            if (item.kind === 'pampTodo') {
              selectedFromSet[item.id] = true;
            }
          });
        }
        templates.forEach(template => {
          const id = template && template.id;
          if (id) {
            groupControls[id] = new FormControl(!!selectedFromSet[id]);
            nameMap[id] = template.title || template.name || id;
          }
        });
        this.pampTodosFormGroup = new FormGroup(groupControls);
        this.pampTodoKeyToFriendlyNameMap = nameMap;
        this.showPatientAppCategory = templates.length > 0;
      });
  }

  private collectSelectedItems(): OneClickQRESetItem[] {
    return collectSelectedSetItems(
      this.questionnaireTypesFormGroup,
      this.pampTodosFormGroup,
      this.questionnaireLabelMaps()
    );
  }

  private questionnaireLabelMaps(): QuestionnaireLabelMaps {
    return {
      standardKeyToName: this.standardKeyToName,
      customKeyToName:
        this.questionnaireRequestService.customQuestionnaireKeyToFriendlyNameMap || {},
      consentKeyToName: this.consentSendService.getConsentFormKeyToFriendlyNameMap(),
      intakeKeyToName: this.intakeKeyToName,
      pampTodoKeyToName: this.pampTodoKeyToFriendlyNameMap
    };
  }
}
